import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Download, Mail, MessageCircle, CheckCircle2, XCircle, Send, ArrowLeft, Pencil, Copy, Link as LinkIcon, Eye, Bell, Receipt, Wallet, Calendar, Database, Settings2, Trash2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BudgetDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [b, setB] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [showBc3Export, setShowBc3Export] = useState(false);
  const [bc3Opts, setBc3Opts] = useState({ include_descomposed: false, include_mediciones: false });

  const load = () => api.get(`/budgets/${id}`).then((r) => { setB(r.data); setEmailTo(r.data.client_email || ""); });

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    let revokeUrl;
    const fetchPdf = async () => {
      try {
        const resp = await api.get(`/budgets/${id}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(resp.data);
        revokeUrl = url;
        setPdfUrl(url);
      } catch (e) { /* ignore */ }
    };
    fetchPdf();
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
  }, [id, b?.status, b?.updated_at]);

  if (!b) return <div className="text-slate-500">Cargando…</div>;

  const setStatus = async (status) => {
    await api.put(`/budgets/${id}/status`, { status });
    toast.success("Estado actualizado");
    load();
  };

  const downloadPdf = async () => {
    const resp = await api.get(`/budgets/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url; a.download = `${b.number}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportBc3 = async () => {
    try {
      const params = new URLSearchParams();
      if (bc3Opts.include_descomposed) params.set("include_descomposed", "1");
      if (bc3Opts.include_mediciones) params.set("include_mediciones", "1");
      const resp = await api.get(`/bc3/export/${id}?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${b.number}.bc3`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Archivo BC3 descargado");
      setShowBc3Export(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error exportando BC3");
    }
  };

  const sendEmail = async () => {
    if (!emailTo) return toast.error("Indica un email");
    try {
      const { data } = await api.post(`/budgets/${id}/email`, { to: emailTo });
      if (data.mocked) toast.warning("Email guardado (modo MOCKED — añade RESEND_API_KEY para envío real)");
      else toast.success("Email enviado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/budgets/${id}/duplicate`);
      toast.success("Presupuesto duplicado");
      nav(`/budgets/${data.id}/edit`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const deleteBudget = async () => {
    if (!window.confirm(`¿Borrar el presupuesto ${b.number}?\n\nEsta acción no se puede deshacer y el enlace público dejará de funcionar.`)) return;
    try {
      await api.delete(`/budgets/${id}`);
      toast.success("Presupuesto borrado");
      nav("/budgets");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error borrando"); }
  };

  const remind = async () => {
    try {
      const { data } = await api.post(`/budgets/${id}/remind`);
      if (data.mocked) toast.warning("Recordatorio guardado (modo MOCKED)");
      else toast.success("Recordatorio enviado al cliente");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const createInvoice = async () => {
    try {
      const { data } = await api.post(`/budgets/${id}/invoice`);
      if (data.mocked) toast.warning("Factura simulada (configura Holded/Quipu en Perfil para crear facturas reales)");
      else toast.success(`Factura creada en ${data.provider}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error creando factura"); }
  };

  const createInternalInvoice = async () => {
    try {
      const { data } = await api.post(`/invoices/from-budget/${id}`);
      toast.success("Factura borrador creada en Contabilidad");
      nav(`/accounting`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error creando factura"); }
  };

  const markPaid = async () => {
    try {
      await api.put(`/budgets/${id}/payment`, { payment_status: "paid" });
      toast.success("Marcado como cobrado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const markPending = async () => {
    try {
      await api.put(`/budgets/${id}/payment`, { payment_status: "pending" });
      toast.success("Marcado como pendiente");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const updateExpectedDate = async (e) => {
    const date = e.target.value;
    try {
      await api.put(`/budgets/${id}/payment`, { expected_payment_date: date });
      toast.success("Fecha actualizada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const publicLink = b.public_token ? `${window.location.origin}/p/${b.public_token}` : "";
  const copyPublicLink = () => {
    if (!publicLink) return;
    navigator.clipboard.writeText(publicLink);
    toast.success("Enlace público copiado");
  };

  const whatsappLink = () => {
    const phone = (b.client_phone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(`Hola ${b.client_name}, le envío el presupuesto ${b.ai_data?.title} (Total: ${b.ai_data?.total?.toFixed(2)} €). Puede revisarlo, aceptarlo o firmarlo aquí: ${publicLink}`);
    return phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
  };

  return (
    <div data-testid="budget-detail">
      <Link to="/budgets" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Volver</Link>

      <div className="mt-3 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar">{b.number}</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight mt-2">{b.ai_data?.title}</h1>
          <p className="text-slate-500 mt-1">Cliente: <span className="font-semibold text-slate-700">{b.client_name}</span> · Total: <span className="font-bold text-slate-900">{b.ai_data?.total?.toFixed(2)} €</span></p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/budgets/${id}/edit`} data-testid="btn-edit-budget" className="h-11 px-4 border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold"><Pencil className="w-4 h-4" /> Editar</Link>
          <button onClick={duplicate} data-testid="btn-duplicate" className="h-11 px-4 border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold"><Copy className="w-4 h-4" /> Duplicar</button>
          <button onClick={downloadPdf} data-testid="btn-download-pdf" className="h-11 px-4 border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold"><Download className="w-4 h-4" /> PDF</button>
          <button onClick={() => setShowBc3Export(true)} data-testid="btn-export-bc3" className="h-11 px-4 border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold"><Database className="w-4 h-4" /> Exportar BC3</button>
          <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="btn-whatsapp" className="h-11 px-4 inline-flex items-center gap-2 text-sm font-semibold text-white" style={{ background: "#25D366" }}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          <button onClick={deleteBudget} data-testid="btn-delete-budget" title="Borrar presupuesto" aria-label="Borrar presupuesto" className="h-11 w-11 border border-slate-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 inline-flex items-center justify-center text-slate-500"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Public link banner */}
      <div className="mt-6 bg-orange-50 border border-orange-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <LinkIcon className="w-5 h-5 text-orange-600 flex-shrink-0" />
          <div className="min-w-0">
            <div className="kbar text-orange-600">Enlace público para el cliente</div>
            <div className="text-sm text-slate-700 truncate font-mono" data-testid="public-link">{publicLink}</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={copyPublicLink} data-testid="copy-public-link" className="h-10 px-4 bg-white border border-slate-200 hover:border-slate-300 text-sm font-semibold">Copiar enlace</button>
          <a href={publicLink} target="_blank" rel="noreferrer" data-testid="open-public-link" className="h-10 px-4 btn-brand inline-flex items-center gap-2 text-sm"><Eye className="w-4 h-4" /> Vista cliente</a>
        </div>
      </div>

      {/* View tracking + reminder */}
      {b.status === "sent" && (
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3 bg-slate-50 border border-slate-200 px-4 py-3">
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-slate-700"><Eye className="w-4 h-4 text-slate-400" /> <span className="font-bold tabular-nums">{b.views || 0}</span> {b.views === 1 ? "vista" : "vistas"}</span>
            {b.first_viewed_at && <span className="text-xs text-slate-500">Visto por primera vez: {new Date(b.first_viewed_at).toLocaleString("es-ES")}</span>}
            {b.reminder_sent_at && <span className="text-xs text-blue-600">Recordatorio enviado: {new Date(b.reminder_sent_at).toLocaleDateString("es-ES")}</span>}
          </div>
          <button onClick={remind} disabled={!b.client_email} data-testid="btn-remind" title={!b.client_email ? "El cliente no tiene email" : "Enviar recordatorio ahora"} className="h-10 px-4 bg-white border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold disabled:opacity-40">
            <Bell className="w-4 h-4" /> Enviar recordatorio
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mt-6">
        <div className="bg-white lg:col-span-2 min-h-[600px]">
          {pdfUrl ? (
            <iframe data-testid="pdf-iframe" title="PDF preview" src={pdfUrl} className="w-full h-[80vh] min-h-[600px]" />
          ) : (
            <div className="p-8 text-slate-500">Cargando PDF…</div>
          )}
        </div>

        <div className="bg-white p-6 space-y-6">
          <div>
            <div className="kbar mb-3">Estado</div>
            <div className="grid grid-cols-2 gap-2">
              <Btn icon={Send} label="Enviado" active={b.status === "sent"} onClick={() => setStatus("sent")} testid="status-sent" />
              <Btn icon={CheckCircle2} label="Aceptado" active={b.status === "accepted"} onClick={() => setStatus("accepted")} testid="status-accepted" success />
              <Btn icon={XCircle} label="Rechazado" active={b.status === "rejected"} onClick={() => setStatus("rejected")} testid="status-rejected" danger />
              <Btn label="Borrador" active={b.status === "draft"} onClick={() => setStatus("draft")} testid="status-draft" />
            </div>
            {b.signature && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-sm">
                <div className="font-bold text-blue-900">✍ Firmado digitalmente</div>
                <div className="text-xs text-blue-700">por {b.signature.signer_name} · {new Date(b.signed_at).toLocaleString("es-ES")}</div>
              </div>
            )}

            {b.status === "accepted" && (
              <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
                <div>
                  <div className="kbar mb-2">Cobro</div>
                  <div className="flex items-center gap-2">
                    {b.payment_status === "paid" ? (
                      <button onClick={markPending} data-testid="btn-mark-pending" className="h-10 px-3 text-sm font-bold inline-flex items-center gap-2 bg-green-600 text-white hover:bg-green-700">
                        <Wallet className="w-4 h-4" /> Cobrado ✓ · Cambiar
                      </button>
                    ) : (
                      <button onClick={markPaid} data-testid="btn-mark-paid" className="h-10 px-3 text-sm font-bold inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-green-600 hover:text-green-700">
                        <Wallet className="w-4 h-4" /> Marcar como cobrado
                      </button>
                    )}
                  </div>
                  {b.paid_at && <div className="text-xs text-green-700 mt-1.5">Cobrado el {new Date(b.paid_at).toLocaleDateString("es-ES")}</div>}
                  {b.payment_status !== "paid" && (
                    <div className="mt-3">
                      <label className="kbar block text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> Fecha prevista de cobro</label>
                      <input
                        type="date"
                        data-testid="expected-date"
                        value={b.expected_payment_date || ""}
                        onChange={updateExpectedDate}
                        className="w-full h-10 mt-1 px-2 border border-slate-200 outline-none focus:border-orange-600 text-sm"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Si no la indicas: 7 días tras la aceptación.</p>
                    </div>
                  )}
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <div className="kbar mb-2">Facturación</div>
                  {b.invoice_doc_id ? (
                    <Link to="/accounting" className="block p-3 bg-green-50 border border-green-200 text-sm hover:bg-green-100 transition-colors" data-testid="invoice-doc-badge">
                      <div className="font-bold text-green-900 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Factura interna creada</div>
                      <div className="text-xs text-green-700 mt-1">Editar y emitir en Contabilidad →</div>
                    </Link>
                  ) : b.invoice_id ? (
                    <div className="p-3 bg-green-50 border border-green-200 text-sm">
                      <div className="font-bold text-green-900 inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Factura creada</div>
                      <div className="text-xs text-green-700 mt-1">ID: <span className="font-mono">{b.invoice_id}</span> · {b.invoice_provider}</div>
                      {b.invoice_url && b.invoice_url !== "#" && (
                        <a href={b.invoice_url} target="_blank" rel="noreferrer" className="text-xs text-green-700 underline">Ver factura →</a>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button onClick={createInternalInvoice} data-testid="btn-create-internal-invoice" className="h-11 w-full btn-brand inline-flex items-center justify-center gap-2">
                        <Receipt className="w-4 h-4" /> Crear factura (Verifactu)
                      </button>
                      <button onClick={createInvoice} data-testid="btn-create-invoice" className="h-10 w-full border border-slate-200 hover:border-slate-300 inline-flex items-center justify-center gap-2 text-xs font-semibold text-slate-600">
                        Holded / Quipu externa
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="kbar mb-3">Enviar por email</div>
            <input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} type="email" placeholder="email@cliente.com" className="w-full h-11 px-3 border border-slate-200 focus:border-orange-600 outline-none" data-testid="email-to" />
            <button onClick={sendEmail} data-testid="btn-send-email" className="btn-brand h-11 w-full mt-2 inline-flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> Enviar PDF</button>
            <p className="text-xs text-slate-400 mt-2">Se enviará con el PDF + enlace al presupuesto online (aceptar/firmar/rechazar).</p>
          </div>

          <div>
            <div className="kbar mb-3">Resumen</div>
            <div className="text-sm text-slate-600">{b.ai_data?.summary}</div>
            <dl className="mt-3 text-sm space-y-1">
              <Row label="Subtotal" v={`${b.ai_data?.subtotal?.toFixed(2)} €`} />
              <Row label={b.ai_data?.iva_rate === 0 ? "Sin impuesto" : `${b.ai_data?.tax_label || "IVA"} ${b.ai_data?.iva_rate}%`} v={`${b.ai_data?.iva_amount?.toFixed(2)} €`} />
              <Row label="Total" v={`${b.ai_data?.total?.toFixed(2)} €`} bold />
            </dl>
          </div>
        </div>
      </div>

      {showBc3Export && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowBc3Export(false)}>
          <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="bc3-export-modal">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-5 h-5 text-orange-600" />
              <div className="text-lg font-semibold">Exportar a BC3 (FIEBDC-3)</div>
            </div>
            <p className="text-xs text-slate-500 mb-3">Genera un archivo .bc3 compatible con software de presupuestos del sector construcción.</p>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input
                type="checkbox"
                checked={bc3Opts.include_descomposed}
                onChange={(e) => setBc3Opts({ ...bc3Opts, include_descomposed: e.target.checked })}
                data-testid="bc3-export-descomposed"
              />
              Incluir descompuestos (auxiliares)
            </label>
            <label className="flex items-center gap-2 text-sm mb-3">
              <input
                type="checkbox"
                checked={bc3Opts.include_mediciones}
                onChange={(e) => setBc3Opts({ ...bc3Opts, include_mediciones: e.target.checked })}
                data-testid="bc3-export-mediciones"
              />
              Incluir mediciones
            </label>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowBc3Export(false)} className="px-4 py-2 text-sm">Cancelar</button>
              <button onClick={exportBc3} data-testid="bc3-export-confirm" className="px-4 py-2 bg-orange-600 text-white text-sm font-semibold">
                Descargar .bc3
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Btn({ icon: Icon, label, active, onClick, testid, success, danger }) {
  const base = "h-11 px-3 text-sm font-bold inline-flex items-center justify-center gap-2 border transition-colors";
  let cls = "bg-white border-slate-200 hover:border-slate-300 text-slate-700";
  if (active) {
    if (success) cls = "bg-green-600 border-green-600 text-white";
    else if (danger) cls = "bg-red-600 border-red-600 text-white";
    else cls = "bg-slate-900 border-slate-900 text-white";
  }
  return (
    <button onClick={onClick} data-testid={testid} className={`${base} ${cls}`}>
      {Icon && <Icon className="w-4 h-4" />} {label}
    </button>
  );
}

function Row({ label, v, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={bold ? "font-bold text-slate-900" : "text-slate-700 tabular-nums"}>{v}</span>
    </div>
  );
}
